import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Tooltip,
	Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import axios from "axios";
import { useState } from "react";

interface Props {
	open: boolean;
	onClose: () => void;
	userId: string;
	userEmail: string;
}

interface SetTemporaryPasswordResponse {
	password: string;
}

// Backup-path UX for the Keycloak cutover: admin sets a one-time password the
// external Carlos user can use immediately (over-the-phone hand-off, for when
// Mailjet drops the reset link). Password is shown ONCE — the dialog never
// re-renders it after Schließen, and a fresh call generates a fresh password
// server-side. Mirrors the equivalent dialog in servicefreigabe-ui
// (src/pages/intern/users/components/SetTemporaryPasswordDialog.tsx) so support
// staff who manage both apps see the same shape.
export default function SetTemporaryPasswordDialog({ open, onClose, userId, userEmail }: Props) {
	const [error, setError] = useState<string | null>(null);
	const [password, setPassword] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	async function handleConfirm() {
		setSubmitting(true);
		setError(null);
		try {
			const { data } = await axios.post<SetTemporaryPasswordResponse>(
				`/api/users/${userId}/set-temporary-password`
			);
			setPassword(data.password);
		} catch (err) {
			// axios 0.21 doesn't export AxiosError as a value, so duck-type the response shape.
			const response = (err as { response?: { data?: unknown } } | undefined)?.response;
			if (response) {
				setError(typeof response.data === "string" ? response.data : "Ein Fehler ist aufgetreten");
			} else {
				setError("Ein Fehler ist aufgetreten");
			}
		} finally {
			setSubmitting(false);
		}
	}

	function handleClose() {
		setError(null);
		setPassword(null);
		setCopied(false);
		setSubmitting(false);
		onClose();
	}

	async function handleCopy() {
		if (!password) return;
		await navigator.clipboard.writeText(password);
		setCopied(true);
		window.setTimeout(() => setCopied(false), 2000);
	}

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>Temporäres Passwort setzen</DialogTitle>
			<DialogContent>
				{password ? (
					<Box mt={1}>
						<Alert severity="warning" sx={{ mb: 2 }}>
							Das Passwort wird nur einmal angezeigt. Geben Sie es ausschließlich
							persönlich an den Benutzer weiter, telefonisch oder von Ihrer
							Ayvens-Mailadresse. Nicht in Tickets, Slack oder ähnlichem teilen.
						</Alert>
						<Typography gutterBottom>
							Benutzer: <strong>{userEmail}</strong>
						</Typography>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
								p: 1.5,
								// Theme-aware highlight: subtle contrast in both light and dark
								// modes. SF's dialog hard-coded grey.100, which is illegible
								// against a dark background — Carlos has a Recoil-driven theme
								// toggle, so the password box must follow the palette.
								bgcolor: "action.hover",
								borderRadius: 1,
								mt: 2,
							}}
						>
							<Typography
								sx={{
									fontFamily: "monospace",
									fontSize: "1.2rem",
									letterSpacing: "0.05em",
									flex: 1,
									// Explicit colour so the monospace text follows the theme
									// instead of whatever Typography inherits in this surface.
									color: "text.primary",
								}}
							>
								{password}
							</Typography>
							<Tooltip title={copied ? "Kopiert" : "In Zwischenablage kopieren"}>
								<IconButton size="small" onClick={handleCopy}>
									<ContentCopyIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						</Box>
						<Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
							Der Benutzer kann sich mit diesem Passwort sofort anmelden. Es ist kein
							Passwortwechsel beim ersten Login erforderlich.
						</Typography>
					</Box>
				) : (
					<Box mt={1}>
						{error && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{error}
							</Alert>
						)}
						<Typography gutterBottom>
							Für <strong>{userEmail}</strong> ein einmaliges Passwort erzeugen?
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Verwenden Sie diese Option nur, wenn die Passwort-Reset-Mail den
							Benutzer nicht erreicht hat. Das Passwort wird Ihnen einmalig
							angezeigt und ist sofort für die Anmeldung nutzbar. Übergeben Sie es
							persönlich (Telefon oder Ayvens-Mail).
						</Typography>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				{password ? (
					<Button onClick={handleClose}>Schließen</Button>
				) : (
					<>
						<Button onClick={handleClose}>Abbrechen</Button>
						<Button variant="contained" onClick={handleConfirm} disabled={submitting}>
							Passwort erzeugen
						</Button>
					</>
				)}
			</DialogActions>
		</Dialog>
	);
}
